import {
  CheckoutProvider,
  ExpressCheckoutElement,
  useCheckout,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useOrganization } from "~hooks";
import { useRouter } from "next/router";
import { sdk } from "~lib";
import { useMemo, useState } from "react";
import { Modal } from "~components";
import { Container, PrimaryButton, SecondaryButton, Stack } from "flicket-ui";
import { on } from "process";
import CustomModal from "~components/CustomModal";

export const ExpressCheckoutSection = () => {
  const { query } = useRouter();

  const [publishableKey, setPublishableKey] = useState<string | null>(
    process.env.STRIPE_PUBLISHABLE_KEY
  );

  const { organization } = useOrganization();

  const stripe = useMemo(
    () =>
      loadStripe(publishableKey, {
        developerTools: {
          assistant: {
            enabled: false,
          },
        },
      }),
    [publishableKey]
  );

  const fetchClientSecret = async () => {
    const response = await sdk({
      orgId: organization.id,
    }).createCheckoutSession({
      input: {
        eventId: query.eventId as string,
        membershipId: query.membershipId as string,
        successUrl: `${window.location.origin}/checkout/success`,
        cancelUrl: `${window.location.origin}/checkout/cancel`,
      },
    });
    setPublishableKey(response.createCheckoutSession?.publishableKey);
    console.log("Response from createCheckoutSession:", response);
    return response.createCheckoutSession?.clientSecret; // TODO: wrong prop name bro
  };

  return (
    <>
      <CheckoutProvider
        stripe={stripe}
        options={{
          fetchClientSecret: async () => {
            console.log("Fetching client secret for session");
            await Promise.resolve(); // Simulate async operation
            const clientSecret = await fetchClientSecret();
            console.log("Fetched client secret:", clientSecret);
            return clientSecret;
          },
        }}
      >
        <ExpressCheckoutElementWrapper
          stripe={stripe}
          fetchClientSecret={fetchClientSecret}
        />
      </CheckoutProvider>
    </>
  );
};

const ExpressCheckoutElementWrapper = ({
  stripe,
  fetchClientSecret,
}: {
  stripe?: Promise<Stripe>;
  fetchClientSecret?: () => Promise<string | undefined>;
} = {}) => {
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<{
    googlePay?: boolean;
    applePay?: boolean;
    link?: boolean;
  }>({
    googlePay: false,
    applePay: false,
    link: false,
  });

  const [expressCheckoutModal, setShowExpressCheckoutModal] = useState<
    "none" | "googlePay" | "applePay" | "link"
  >("none");

  return (
    <>
      <div
        style={{
          display: "none",
        }}
      >
        <ExpressCheckoutElement
          onConfirm={() => {
            // do nothing
          }}
          onReady={(e) => {
            console.log("stripe checkout ready", e);
            if (!e.availablePaymentMethods) return;
            setAvailablePaymentMethods({
              googlePay: e.availablePaymentMethods.googlePay,
              applePay: e.availablePaymentMethods.applePay,
              link: e.availablePaymentMethods.link,
            });
          }}
        />
      </div>
      {(availablePaymentMethods.googlePay || (window as any).gPay) && (
        <div
          style={{
            width: "100%",
            marginBottom: "8px",
            height: "40px",
            minWidth: "234px",
          }}
        >
          <button
            style={{
              width: "100%",
              height: "48px",
              backgroundColor: "#000",
              border: "none",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: "0 16px",
              backgroundImage:
                "url(https://www.gstatic.com/instantbuy/svg/dark/checkout/en.svg)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "147px 20px",
              minWidth: "184px",
            }}
            onClick={() => {
              setShowExpressCheckoutModal("googlePay");
            }}
          />
        </div>
      )}
      {(availablePaymentMethods.applePay || (window as any).aPay) && (
        <div
          style={{
            width: "100%",
            marginBottom: "8px",
            height: "40px",
            minWidth: "234px",
          }}
        >
          <button
            style={{
              width: "100%",
              height: "48px",
              lineHeight: "48px",
              backgroundColor: "#000",
              border: "none",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: "0 16px",
              color: "#fff",
              fontWeight: 500,
              fontSize: "15px",
            }}
            onClick={() => {
              setShowExpressCheckoutModal("applePay");
            }}
          >
            Checkout with&nbsp;
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="41"
              height="25"
              viewBox="0 0 39 16"
              fill="none"
            >
              <path
                d="M7.12604 2.05766C6.66895 2.59854 5.93762 3.02515 5.20629 2.96421C5.11487 2.23287 5.47292 1.45583 5.89191 0.975894C6.349 0.419776 7.14889 0.0236363 7.79643 -0.00683594C7.87261 0.75497 7.5755 1.50154 7.12604 2.05766ZM7.78881 3.10895C6.7299 3.04801 5.82335 3.71078 5.32056 3.71078C4.81015 3.71078 4.04072 3.13942 3.20274 3.15466C2.11335 3.1699 1.10015 3.78696 0.544032 4.76969C-0.598677 6.73515 0.246928 9.64525 1.35155 11.245C1.89243 12.0373 2.53996 12.9058 3.39319 12.8753C4.2007 12.8448 4.52066 12.3497 5.49577 12.3497C6.4785 12.3497 6.76037 12.8753 7.61359 12.8601C8.49729 12.8448 9.05341 12.0678 9.59429 11.2755C10.2114 10.3766 10.4627 9.50051 10.478 9.4548C10.4627 9.43956 8.77154 8.79203 8.7563 6.8418C8.74107 5.21154 10.0895 4.43449 10.1504 4.38879C9.3886 3.26131 8.20018 3.13942 7.78881 3.10895ZM13.9061 0.899713V12.7763H15.7497V8.71585H18.3017C20.6329 8.71585 22.2707 7.11605 22.2707 4.80016C22.2707 2.48427 20.6633 0.899713 18.3627 0.899713H13.9061ZM15.7497 2.4538H17.8751C19.4749 2.4538 20.3891 3.30702 20.3891 4.80778C20.3891 6.30854 19.4749 7.16938 17.8675 7.16938H15.7497V2.4538ZM25.6379 12.8677C26.7959 12.8677 27.87 12.2811 28.3576 11.3517H28.3957V12.7763H30.1021V6.86466C30.1021 5.15059 28.7309 4.04597 26.6207 4.04597C24.6628 4.04597 23.2154 5.16583 23.1621 6.70468H24.8228C24.9599 5.97334 25.6379 5.4934 26.5673 5.4934C27.6948 5.4934 28.3271 6.01905 28.3271 6.98655V7.6417L26.0265 7.77882C23.8858 7.90833 22.7278 8.78441 22.7278 10.308C22.7278 11.8469 23.9239 12.8677 25.6379 12.8677ZM26.1331 11.4583C25.1504 11.4583 24.5257 10.986 24.5257 10.2623C24.5257 9.51574 25.1275 9.08151 26.2778 9.01295L28.3271 8.88344V9.55383C28.3271 10.6661 27.3825 11.4583 26.1331 11.4583ZM32.3799 16.0063C34.1778 16.0063 35.0234 15.3207 35.7623 13.241L39 4.16024H37.126L34.9548 11.1765H34.9167L32.7456 4.16024H30.8182L33.9416 12.8067L33.774 13.3324C33.4921 14.2237 33.0351 14.5665 32.2199 14.5665C32.0752 14.5665 31.7933 14.5513 31.6791 14.536V15.9606C31.7857 15.9911 32.2428 16.0063 32.3799 16.0063Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      )}
      {expressCheckoutModal !== "none" && (
        <ExpressCheckoutModal
          paymentMethod={expressCheckoutModal}
          onClose={() => setShowExpressCheckoutModal("none")}
          stripe={stripe}
          fetchClientSecret={fetchClientSecret}
        />
      )}
    </>
  );
};

const ExpressCheckoutModal = ({
  paymentMethod,
  onClose,
  stripe,
  fetchClientSecret,
}: {
  paymentMethod: "googlePay" | "applePay" | "link";
  onClose: () => void;
  stripe?: Promise<Stripe> | undefined;
  fetchClientSecret?: () => Promise<string | undefined>;
}) => {
  let label = "";
  switch (paymentMethod) {
    case "googlePay":
      label = "Google Pay";
      break;
    case "applePay":
      label = "Apple Pay";
      break;
    case "link":
      label = "Link";
      break;
  }

  return (
    // <CheckoutProvider
    //   stripe={stripe}
    //   options={{
    //     fetchClientSecret: async () => {
    //       console.log("Fetching client secret for session");
    //       await Promise.resolve(); // Simulate async operation
    //       const clientSecret = await fetchClientSecret();
    //       console.log("Fetched client secret:", clientSecret);
    //       return clientSecret;
    //     },
    //   }}
    // >
    <CustomModal
      isOpen={true}
      close={onClose}
      title={`Express Checkout with ${label}`}
      size="small"
    >
      <CustomModal.Header>
        <h2>Express Checkout with {label}</h2>
      </CustomModal.Header>
      <CustomModal.Content>
        <p>Complete your payment using {label}.</p>
      </CustomModal.Content>
      <CustomModal.Footer>
        <Stack direction={"horizontal"} gap={2} justifyContent="flex-end">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={onClose}>
            <span>Pay with {label}</span>
          </PrimaryButton>
          {/* <ExpressCheckoutElement
            onConfirm={() => {
              // do nothing
            }}
            onReady={(e) => {
              console.log("proper stripe checkout ready", e);
            }}
            options={{
              paymentMethods: {
                applePay: paymentMethod === "applePay" ? "always" : "never",
                googlePay: paymentMethod === "googlePay" ? "always" : "never",
                link: paymentMethod === "link" ? "auto" : "never",
                amazonPay: "never",
                klarna: "never",
                paypal: "never",
              },
            }}
          ></ExpressCheckoutElement> */}
        </Stack>
      </CustomModal.Footer>
    </CustomModal>
    // </CheckoutProvider>
  );
};
